import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CheckCircle2, FileCheck, Loader2 } from "lucide-react";
import { mapLegacyStatusToNewStatus, getStatusIndex } from "@/lib/proposalStatus";

interface ProposalConfirmationProps {
  status: string | null | undefined;
  isAccepting: boolean;
  onAccept: () => void;
  requireTermsAcceptance?: boolean;
  t: {
    readyToConfirm: string;
    confirmationDescription: string;
    acceptProposal: string;
    proposalConfirmed: string;
    thankYouMessage: string;
    acceptTermsLabel: string;
    acceptTermsRequiredError: string;
  };
}

export const ProposalConfirmation: React.FC<ProposalConfirmationProps> = ({
  status,
  isAccepting,
  onAccept,
  requireTermsAcceptance = false,
  t,
}) => {
  const [hasAcceptedTerms, setHasAcceptedTerms] = React.useState(false);
  const [showTermsError, setShowTermsError] = React.useState(false);
  const normalizedStatus = mapLegacyStatusToNewStatus(status);
  const statusIndex = getStatusIndex(normalizedStatus);
  const sentIndex = getStatusIndex("sent");
  
  const canAccept = normalizedStatus === "sent" || normalizedStatus === "needs_fix_sales";
  
  const isConfirmed = statusIndex > sentIndex && normalizedStatus !== "needs_fix_sales";

  const handleTermsChange = (checked: boolean | "indeterminate") => {
    const accepted = checked === true;
    setHasAcceptedTerms(accepted);
    if (accepted) {
      setShowTermsError(false);
    }
  };

  const handleAcceptClick = () => {
    if (requireTermsAcceptance && !hasAcceptedTerms) {
      setShowTermsError(true);
      return;
    }
    onAccept();
  };

  const renderTermsAcceptance = ({
    checked,
    disabled = false,
    showError = false,
  }: {
    checked: boolean;
    disabled?: boolean;
    showError?: boolean;
  }) => (
    <div className="mx-auto flex max-w-full flex-col items-center gap-2">
      <div className="inline-flex max-w-full items-center gap-3 rounded-xl border border-border/70 bg-background/70 px-4 py-3 text-left shadow-sm">
        <Checkbox
          id="accept-terms"
          checked={checked}
          onCheckedChange={disabled ? undefined : handleTermsChange}
          className="shrink-0"
          aria-invalid={showError}
          disabled={disabled}
        />
        <Label
          htmlFor="accept-terms"
          className={`max-w-[min(42rem,calc(100vw-7rem))] leading-relaxed text-sm font-medium text-foreground ${
            disabled ? "cursor-default" : "cursor-pointer"
          }`}
        >
          {t.acceptTermsLabel}
        </Label>
      </div>
      {showError && (
        <p className="text-center text-sm font-medium text-destructive">
          {t.acceptTermsRequiredError}
        </p>
      )}
    </div>
  );

  if (canAccept) {
    return (
      <Card className="border-primary/30 shadow-2xl bg-gradient-to-br from-primary/5 via-card to-accent/5">
        <CardContent className="py-12">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary text-primary-foreground shadow-lg mb-4">
              <FileCheck className="h-10 w-10" />
            </div>

            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-2">{t.readyToConfirm}</h3>
              <p className="text-muted-foreground max-w-xl mx-auto">{t.confirmationDescription}</p>
            </div>

            <Button
              size="lg"
              onClick={handleAcceptClick}
              disabled={isAccepting}
              className="mt-4 min-w-[220px] text-lg h-14 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all"
            >
              {isAccepting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-5 w-5" />
                  {t.acceptProposal}
                </>
              )}
            </Button>

            {requireTermsAcceptance && renderTermsAcceptance({ checked: hasAcceptedTerms, showError: showTermsError })}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isConfirmed) {
    return (
      <Card className="border-green-500/30 shadow-2xl bg-gradient-to-br from-green-50 via-card to-green-50 dark:from-green-950/20 dark:via-card dark:to-green-950/20">
        <CardContent className="py-12">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-green-600 mb-4 shadow-lg">
              <CheckCircle2 className="h-10 w-10 text-white" />
            </div>

            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-green-700 dark:text-green-400 mb-2">{t.proposalConfirmed}</h3>
              <p className="text-muted-foreground max-w-xl mx-auto">{t.thankYouMessage}</p>
            </div>

            {requireTermsAcceptance && renderTermsAcceptance({ checked: true, disabled: true })}
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default ProposalConfirmation;
